import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DropDownListModule } from '@progress/kendo-angular-dropdowns';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import {
  OUTLOOK_SYNC_FREQUENCY,
  OutlookSyncConfigurationPayload,
  OutlookSyncHistoryEntry,
  OutlookSyncType as OutlookSyncApiType,
  UserSyncConfiguration,
  UserSyncConfigurationService,
} from '@maximizer/outlook-sync/data-access';
import { HistoryGridComponent } from '../../components/history-grid/history-grid.component';
import { OutlookSyncFormGroup } from '../../../form.model';
import { SwitchModule } from '@progress/kendo-angular-inputs';
import { TooltipModule } from '@progress/kendo-angular-tooltip';
import {
  loadSyncHistory,
  resolveConfigurationId,
} from '../../utils/history-mapper.util';

@Component({
  selector: 'maximizer-outlook-task-tab',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DropDownListModule,
    TranslatePipe,
    HistoryGridComponent,
    SwitchModule,
    TooltipModule,
  ],
  templateUrl: './outlook-task-tab.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OutlookTaskTabComponent implements OnInit {
  private readonly outlookSync = inject(UserSyncConfigurationService);
  private readonly destroyRef = inject(DestroyRef);

  configuration = input.required<UserSyncConfiguration | null>();
  form = input.required<OutlookSyncFormGroup>();
  payload = input.required<OutlookSyncConfigurationPayload | null>();

  protected readonly history = signal<OutlookSyncHistoryEntry[]>([]);
  protected readonly historyLoading = signal(false);

  protected readonly syncFrequency = OUTLOOK_SYNC_FREQUENCY.tasks;

  private static readonly DEFAULT_TASK_LIST_NAME = 'Maximizer';

  private readonly taskListName = signal<string>(
    OutlookTaskTabComponent.DEFAULT_TASK_LIST_NAME,
  );
  protected readonly taskListOptions = computed<
    ReadonlyArray<{ label: string; value: string }>
  >(() => [{ label: this.taskListName(), value: this.taskListName() }]);
  protected readonly taskListControl = new FormControl<string>(
    OutlookTaskTabComponent.DEFAULT_TASK_LIST_NAME,
    { nonNullable: true },
  );

  private currentConfigurationId: string | null = null;

  constructor() {
    effect(() => {
      this.configuration();
      this.payload();
      this.handleConfigurationChange();
    });
  }

  ngOnInit(): void {
    this.form()
      .statusChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.syncLocalControlsDisabled());
    this.syncLocalControlsDisabled();
  }

  private syncLocalControlsDisabled(): void {
    if (this.form().disabled) {
      this.taskListControl.disable({ emitEvent: false });
    } else {
      this.taskListControl.enable({ emitEvent: false });
    }
  }

  protected refreshHistory(): void {
    const configurationId = this.getConfigurationId();
    loadSyncHistory({
      service: this.outlookSync,
      destroyRef: this.destroyRef,
      syncType: OutlookSyncApiType.Tasks,
      idPrefix: 'tasks-history',
      errorMessage: 'Failed to load tasks sync history',
      configurationId,
      isCurrent: () => this.currentConfigurationId === configurationId,
      history: this.history,
      loading: this.historyLoading,
    });
  }

  private handleConfigurationChange(): void {
    const configurationId = this.getConfigurationId();

    if (!configurationId) {
      this.currentConfigurationId = null;
      this.resetState();
      return;
    }

    if (configurationId === this.currentConfigurationId) {
      this.syncTasksToggle();
      return;
    }

    this.currentConfigurationId = configurationId;
    this.syncTasksToggle();
    const name =
      this.configuration()?.tasksSync?.outlookTodoListName?.trim() ??
      OutlookTaskTabComponent.DEFAULT_TASK_LIST_NAME;
    this.taskListName.set(name);
    this.taskListControl.setValue(name, { emitEvent: false });
    this.refreshHistory();
  }

  private resetState(): void {
    this.history.set([]);
    this.historyLoading.set(false);
    this.form().controls.tasksSyncEnabled.setValue(false, { emitEvent: false });
    this.taskListName.set(OutlookTaskTabComponent.DEFAULT_TASK_LIST_NAME);
    this.taskListControl.setValue(
      OutlookTaskTabComponent.DEFAULT_TASK_LIST_NAME,
      { emitEvent: false },
    );
  }

  private getConfigurationId(): string | null {
    return resolveConfigurationId(this.configuration(), this.payload());
  }

  private syncTasksToggle(): void {
    const control = this.form().controls.tasksSyncEnabled;
    const enabled =
      this.configuration()?.tasksSync?.enabled ??
      this.payload()?.tasksSyncEnabled ??
      control.value ??
      false;
    control.setValue(enabled, { emitEvent: false });
  }
}
